import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Text,
  Flex,
  Avatar,
  Tag,
  Divider,
  Link,
  Spinner,
  Button,
} from "@chakra-ui/react";
import TagFilterSelect from "../../components/TagFilterSelect";
import { InstagramProfileDetailModal } from "../../components/Modals/InstagramProfileDetailModal";
import {
  instagramProfilesTags,
  instagramProfilesList,
} from "../../api";

const INSTAGRAM_BASE = "https://www.instagram.com";
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "-");

export default function InstagramProfilesTab({ keyword = "" }) {
  const [tags, setTags] = useState([]);
  const [contents, setContents] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  const size = 20;

  const loadTags = useCallback(async () => {
    setTagLoading(true);
    try {
      const res = await instagramProfilesTags();
      setTags(res?.data ?? []);
    } catch (e) {
      console.error("태그 조회 실패:", e);
    } finally {
      setTagLoading(false);
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await instagramProfilesList({
        page,
        size,
        tagIds: selectedTagIds.length ? selectedTagIds : undefined,
        keyword: keyword || undefined,
      });
      const data = res?.data;
      setContents(data?.contents ?? []);
      setPageInfo(data?.page ?? null);
    } catch (e) {
      console.error("프로필 목록 조회 실패:", e);
    } finally {
      setLoading(false);
    }
  }, [page, selectedTagIds, keyword]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleTagChange = (nextIds) => {
    setSelectedTagIds(nextIds);
    setPage(1);
  };

  const handleClearTags = () => {
    setSelectedTagIds([]);
    setPage(1);
  };

  return (
    <Box w="100%" minW="0" overflow="hidden">
      {/* 태그 필터 */}
      <Box mb="16px">
        <TagFilterSelect
          tags={tags}
          selectedIds={selectedTagIds}
          onChange={handleTagChange}
          onClearAll={handleClearTags}
          isLoading={tagLoading}
        />
      </Box>

      {/* 테이블 */}
      <Box
        bg="white"
        borderTopRadius="20px"
        boxShadow="sm"
        overflow="hidden"
        overflowX="auto"
      >
        <Box minW="600px">
        <Flex
          py="12px"
          px={{ base: "12px", md: "16px" }}
          gap={{ base: "8px", md: "16px" }}
          bg="#E2EEFF"
          fontWeight="600"
          color="gray.700"
          fontSize={{ base: "xs", md: "sm" }}
        >
          <Box w="8%" flexShrink={0}>프로필</Box>
          <Box w="18%" flex="1" minW="100px">username / 인스타</Box>
          <Box w="15%" minW="80px">연락처</Box>
          <Box w="22%" minW="120px">팔로워/팔로잉/게시물</Box>
          <Box w="15%" minW="70px">인증/비즈니스</Box>
          <Box w="22%" minW="100px">태그</Box>
        </Flex>
        <Divider m="0" />

        {loading ? (
          <Flex justify="center" py="60px">
            <Spinner size="lg" />
          </Flex>
        ) : (
          contents.map((p, idx) => (
            <Box key={p.id}>
              <Flex
                py={{ base: "12px", md: "16px" }}
                minH="72px"
                alignItems="center"
                gap={{ base: "8px", md: "16px" }}
                px={{ base: "12px", md: "16px" }}
                _hover={{ bg: "gray.50" }}
                cursor="pointer"
                onClick={() => setSelectedProfileId(p.id)}
              >
                <Box w="8%" flexShrink={0} pr={{ base: "8px", md: "12px" }}>
                  <Avatar
                    name={p.username}
                    src={p.profilePicUrl || ""}
                    size="sm"
                  />
                </Box>
                <Box w="18%" flex="1" minW="100px">
                  <Text fontWeight="700" fontSize={{ base: "xs", md: "14px" }} noOfLines={1}>
                    @{p.username}
                  </Text>
                  <Link
                    href={`${INSTAGRAM_BASE}/${p.username}`}
                    isExternal
                    fontSize={{ base: "11px", md: "12px" }}
                    color="#0054C0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    인스타에서 보기 →
                  </Link>
                </Box>
                <Box w="15%" fontSize={{ base: "xs", md: "13px" }} minW="80px">
                  {p.contactEmail || "-"}
                </Box>
                <Box w="22%" fontSize={{ base: "xs", md: "13px" }} minW="120px">
                  {fmt(p.followerCount)} / {fmt(p.followingCount)} /{" "}
                  {fmt(p.postCount)}
                </Box>
                <Box w="15%" minW="70px">
                  <Flex gap="4px" flexWrap="wrap">
                    {p.isVerified && (
                      <Tag size="sm" colorScheme="blue">
                        ✓
                      </Tag>
                    )}
                    {p.isBusiness && (
                      <Tag size="sm" colorScheme="purple">
                        B
                      </Tag>
                    )}
                    {!p.isVerified && !p.isBusiness && "-"}
                  </Flex>
                </Box>
                <Box w="22%" minW="100px">
                  <Flex gap="4px" flexWrap="wrap">
                    {p.tags?.length
                      ? p.tags.map((t) => (
                          <Tag key={t.id} size="sm" variant="subtle">
                            #{t.tagName}
                          </Tag>
                        ))
                      : "-"}
                  </Flex>
                </Box>
              </Flex>
              {idx < contents.length - 1 && <Divider m="0" />}
            </Box>
          ))
        )}

        {!loading && contents.length === 0 && (
          <Box p="40px" textAlign="center" color="gray.500">
            조건에 맞는 프로필이 없습니다.
          </Box>
        )}
        </Box>
      </Box>

      {/* 페이지네이션 */}
      {pageInfo && pageInfo.totalPages > 1 && (
        <Flex justify="center" gap="8px" mt="16px">
          <Button
            size="sm"
            isDisabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            이전
          </Button>
          <Text alignSelf="center" fontSize="14px">
            {page} / {pageInfo.totalPages} (총 {pageInfo.totalElements}건)
          </Text>
          <Button
            size="sm"
            isDisabled={!pageInfo.hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </Button>
        </Flex>
      )}

      <InstagramProfileDetailModal
        profileId={selectedProfileId}
        isOpen={!!selectedProfileId}
        onClose={() => setSelectedProfileId(null)}
      />
    </Box>
  );
}
